import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminFetch, buildQueryString } from "./client";
import { queryKeys } from "./queryKeys";
import type { PaginatedResponse } from "./properties";

export interface Alert {
  id: string;
  type: string;
  severity: "critical" | "warning" | "info";
  title: string;
  message: string;
  acknowledged: boolean;
  resolved: boolean;
  created_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
}

export function useAlerts(params: Record<string, unknown>) {
  return useQuery({
    queryKey: queryKeys.alerts.list(params),
    queryFn: () =>
      adminFetch<PaginatedResponse<Alert>>(
        `/api/v1/admin/alerts${buildQueryString(params)}`,
      ),
  });
}

export function useUnacknowledgedAlertCount() {
  return useQuery({
    queryKey: queryKeys.alerts.unacknowledgedCount(),
    queryFn: () =>
      adminFetch<{ count: number }>("/api/v1/admin/alerts/unacknowledged-count"),
    refetchInterval: 30_000,
  });
}

export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      adminFetch(`/api/v1/admin/alerts/${id}/acknowledge`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.alerts.all });
    },
  });
}

export function useResolveAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, resolution }: { id: string; resolution: string }) =>
      adminFetch(`/api/v1/admin/alerts/${id}/resolve`, {
        method: "POST",
        body: JSON.stringify({ resolution }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.alerts.all });
    },
  });
}
